import {buildFormattedBody, type MessageMention, type MessageRoomMention} from './messageFormatting';

const MATRIX_HTML_FORMAT = 'org.matrix.custom.html';

export interface PrivateReplySource {
    senderName: string;
    excerpt: string;
}

export interface PrivateReplyCandidate {
    sender?: string;
    type?: string;
    isRedacted?: boolean;
}

export interface PrivateReplyGate {
    isDirectChat: boolean;
    myUserId?: string | null;
    message?: PrivateReplyCandidate | null;
}

export interface PrivateReplyMessage {
    body: string;
    format?: typeof MATRIX_HTML_FORMAT;
    formattedBody?: string;
}

export function canPrivateReply({isDirectChat, myUserId, message}: PrivateReplyGate): boolean {
    if (isDirectChat || !message || !myUserId) {
        return false;
    }
    const sender = message.sender?.trim();
    if (!sender || sender === myUserId) {
        return false;
    }
    if (message.isRedacted || message.type === 'system') {
        return false;
    }
    return true;
}

export function stripPlainReplyFallback(body: string): string {
    if (!body.startsWith('> ')) {
        return body;
    }
    const lines = body.split('\n');
    let i = 0;
    while (i < lines.length && lines[i].startsWith('>')) {
        i += 1;
    }
    if (i >= lines.length || lines[i].trim() !== '') {
        return body;
    }
    return lines.slice(i + 1).join('\n').trim();
}

export function stripLeadingBlockquote(formattedBody: string): string {
    return formattedBody.replace(
        /^\s*(?:<mx-reply>\s*)?<blockquote[^>]*>[\s\S]*?<\/blockquote>\s*(?:<\/mx-reply>\s*)?/i,
        ''
    );
}

export function privateReplyExcerpt(body: string | null | undefined): string {
    return stripPlainReplyFallback((body ?? '').trim());
}

export function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function buildPrivateReplyQuote(source: PrivateReplySource): {body: string; formattedBody: string} {
    const name = source.senderName.trim() || 'them';
    const excerpt = privateReplyExcerpt(source.excerpt);
    const lines = excerpt.length ? excerpt.split('\n') : [''];
    const body = lines
        .map((line, index) => (index === 0 ? `> <${name}> ${line}` : `> ${line}`))
        .join('\n');
    const formattedBody =
        `<blockquote><strong>${escapeHtml(name)}</strong><br/>` +
        `${lines.map(escapeHtml).join('<br/>')}</blockquote>`;
    return {body, formattedBody};
}

export interface PrivateReplyMediaContent {
    msgtype?: string;
    body?: string;
    filename?: string;
}

export function privateReplyMediaExcerpt(content: PrivateReplyMediaContent): string | null {
    const body = content.body ?? '';
    const filename = content.filename ?? '';
    const caption = filename ? (body && body !== filename ? body : '') : body;
    switch (content.msgtype) {
        case 'm.image':
            return caption ? `📷 ${caption}` : '📷 Image';
        case 'm.video':
            return caption ? `🎥 ${caption}` : '🎥 Video';
        case 'm.audio':
            return caption ? `🎵 ${caption}` : '🎵 Audio';
        case 'm.file':
            return filename ? `📎 ${filename}` : `📎 ${body || 'File'}`;
        default:
            return null;
    }
}

export function composePrivateReplyMessage(
    typed: string,
    source?: PrivateReplySource | null,
    mentions: MessageMention[] = [],
    roomMentions: MessageRoomMention[] = [],
): PrivateReplyMessage {
    const typedFormattedBody = buildFormattedBody(typed, mentions, roomMentions) ?? undefined;
    if (!source) {
        return typedFormattedBody
            ? {body: typed, format: MATRIX_HTML_FORMAT, formattedBody: typedFormattedBody}
            : {body: typed};
    }
    const quote = buildPrivateReplyQuote(source);
    const typedHtml = typedFormattedBody ?? escapeHtml(typed);
    return {
        body: `${quote.body}\n\n${typed}`,
        format: MATRIX_HTML_FORMAT,
        formattedBody: `${quote.formattedBody}${typedHtml}`,
    };
}
